import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

/**
 * Desktop app download URLs, one per OS/architecture build.
 *
 * Field names are the canonical snake_case keys written by admin — never
 * rename or alias them. An empty string or absent field means that build is
 * unavailable and must not be rendered.
 *
 * The `*_beta_url` fields are the pre-release build of the same platform —
 * the stable key with `beta` inserted before `url`. They live on the same
 * document and are only ever surfaced to beta testers.
 *
 * `mac_uninstall_beta_url` and `windows_uninstall_beta_url` are beta-only
 * links to the platform uninstaller. Neither is a build and neither has a
 * stable counterpart.
 *
 * `latest_version` is the version the STABLE urls point at, recorded by hand in
 * admin. It is not a URL and is not a build. The Desktop App page now reads each
 * build's version from its update feed (`desktopFeedService.ts`) and uses this
 * only as a fallback when that feed can't be read.
 */
export interface DesktopDownloads {
  mac_intel_url?: string;
  mac_arm_url?: string;
  windows_intel_url?: string;
  windows_arm_url?: string;
  mac_intel_beta_url?: string;
  mac_arm_beta_url?: string;
  windows_intel_beta_url?: string;
  windows_arm_beta_url?: string;
  mac_uninstall_beta_url?: string;
  windows_uninstall_beta_url?: string;
  latest_version?: string;
}

const DESKTOP_DOWNLOADS_COLLECTION = 'app_downloads';
const DESKTOP_DOWNLOADS_DOC_ID = 'current';

/**
 * Fetch desktop app download links from Firebase
 */
export const fetchDesktopDownloads = async (): Promise<DesktopDownloads | null> => {
  try {
    const docRef = doc(db, DESKTOP_DOWNLOADS_COLLECTION, DESKTOP_DOWNLOADS_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as DesktopDownloads;
    } else {
      return null;
    }
  } catch (error) {
    if (isFirebaseOfflineError(error)) {
      errorReporter.expected('Failed to fetch desktop download links (client offline)', error, {
        feature: 'firebase',
        operation: 'fetchDesktopDownloads',
      });
    } else {
      errorReporter.critical('Failed to fetch desktop download links from Firebase', error, {
        feature: 'settings',
        operation: 'fetchDesktopDownloads',
      });
    }
    return null;
  }
};
